import { useCallback, useEffect, useMemo, useState } from "react";
import { insertRow, listRows, updateRow } from "@/lib/adminApi";
import {
  FARM_STATUS,
  SAFETY_STATUS,
  VERIFICATION_STATUS,
  metaFor,
  optionsFrom,
  type Farm,
} from "@/lib/adminModules";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  TextField,
  btnGhost,
  btnPrimary,
  fieldInput,
  textInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { useAdminToast } from "@/hooks/useAdminToast";

const EMPTY_FARM = {
  name: "",
  farmer_name: "",
  village: "",
  district: "",
  crop: "",
  area: "",
  established: "",
  capacity: 0,
  status: "draft",
  verification: "pending",
  safety: "pending",
};

export default function AdminFarms() {
  const [rows, setRows] = useState<Farm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [verificationFilter, setVerificationFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_FARM });
  const [saving, setSaving] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<Farm>("farms", "name"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load farms.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<Farm>) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("farms", id, patch);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const createFarm = async () => {
    if (!draft.name.trim()) {
      setError("A farm name is required.");
      return;
    }
    setSaving(true);
    try {
      const created = await insertRow<Farm>("farms", {
        ...draft,
        name: draft.name.trim(),
        capacity: Number(draft.capacity) || 0,
        rating: 0,
      });
      setRows((list) => [created, ...list]);
      setCreateOpen(false);
      setDraft({ ...EMPTY_FARM });
      notify(`${created.name} added.`);
    } catch {
      setError("We couldn't create that farm. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (verificationFilter !== "all" && row.verification !== verificationFilter) return false;
      if (!query) return true;
      return [row.name, row.farmer_name || "", row.village || "", row.crop || ""]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [rows, search, statusFilter, verificationFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;
  const avgRating = rows.length
    ? (rows.reduce((sum, row) => sum + Number(row.rating || 0), 0) / rows.length).toFixed(1)
    : "0.0";

  const columns: Column<Farm>[] = [
    {
      key: "name",
      header: "Farm",
      primary: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-md overflow-hidden bg-background-100 shrink-0 flex items-center justify-center text-primary-700">
            {row.image ? (
              <img src={row.image} alt={row.name} className="w-full h-full object-cover object-top" />
            ) : (
              <i className="ri-plant-line"></i>
            )}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.name}</p>
            <p className="text-xs text-foreground-500 mt-0.5">{row.farmer_name || "—"}</p>
          </div>
        </div>
      ),
    },
    {
      key: "village",
      header: "Village",
      render: (row) => (
        <span className="text-sm text-foreground-700 whitespace-nowrap">{row.village || "—"}</span>
      ),
    },
    {
      key: "crop",
      header: "Crop / Type",
      render: (row) => <span className="text-sm text-foreground-700">{row.crop || "—"}</span>,
    },
    {
      key: "capacity",
      header: "Capacity",
      render: (row) => (
        <span className="text-sm text-foreground-700 whitespace-nowrap">{row.capacity} guests</span>
      ),
    },
    {
      key: "verification",
      header: "Verification",
      render: (row) => <StatusPill {...metaFor(VERIFICATION_STATUS, row.verification)} />,
    },
    {
      key: "safety",
      header: "Safety",
      render: (row) => <StatusPill {...metaFor(SAFETY_STATUS, row.safety)} />,
    },
    {
      key: "status",
      header: "Status",
      render: (row) => <StatusPill {...metaFor(FARM_STATUS, row.status)} />,
    },
    {
      key: "rating",
      header: "Rating",
      render: (row) => (
        <span className="inline-flex items-center gap-1 text-sm text-foreground-800">
          <i className="ri-star-fill text-accent-500"></i>
          {row.rating}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Farm Network"
        title="Farms"
        subtitle="Every farm listed on Rythugaru — its location, capacity, verification, safety state and live status."
        actions={
          <button type="button" onClick={() => setCreateOpen(true)} className={btnPrimary}>
            <i className="ri-add-line"></i> New Farm
          </button>
        }
      />

      <StatCards
        stats={[
          { label: "Total farms", value: rows.length, icon: "ri-plant-line", tone: "primary" },
          {
            label: "Active",
            value: rows.filter((row) => row.status === "active").length,
            icon: "ri-checkbox-circle-line",
            tone: "primary",
          },
          {
            label: "Pending approval",
            value: rows.filter((row) => row.status === "pending_approval").length,
            icon: "ri-time-line",
            tone: "accent",
          },
          { label: "Average rating", value: avgRating, icon: "ri-star-line", tone: "secondary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search farms by name, farmer, village…" />
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: "all", label: "All statuses" }, ...optionsFrom(FARM_STATUS)]}
          />
          <Select
            label="Verification"
            value={verificationFilter}
            onChange={setVerificationFilter}
            options={[{ value: "all", label: "All verification" }, ...optionsFrom(VERIFICATION_STATUS)]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">
            {filtered.length} farms
          </span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading farms…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-plant-line"
            title={rows.length === 0 ? "No farms yet" : "No farms match"}
            message={
              rows.length === 0
                ? "Verified farms will appear here once onboarding is complete."
                : "Try a different search or clear the filters."
            }
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.name || "Farm"}
        subtitle="Farm profile"
      >
        {selected ? (
          <>
            {selected.image ? (
              <div className="w-full h-40 rounded-lg overflow-hidden bg-background-100">
                <img
                  src={selected.image}
                  alt={selected.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...metaFor(FARM_STATUS, selected.status)} />
              <StatusPill {...metaFor(VERIFICATION_STATUS, selected.verification)} />
              <StatusPill {...metaFor(SAFETY_STATUS, selected.safety)} />
            </div>

            <DetailList
              items={[
                { icon: "ri-user-star-line", label: "Farmer", value: selected.farmer_name || "—" },
                { icon: "ri-map-pin-line", label: "Village / District", value: `${selected.village} · ${selected.district}` },
                { icon: "ri-plant-line", label: "Crop", value: selected.crop || "—" },
                { icon: "ri-ruler-line", label: "Area", value: selected.area || "—" },
                { icon: "ri-hourglass-line", label: "Established", value: selected.established || "—" },
                { icon: "ri-group-line", label: "Capacity", value: `${selected.capacity} guests` },
              ]}
            />

            {selected.summary ? (
              <p className="text-sm text-foreground-700 leading-relaxed">{selected.summary}</p>
            ) : null}

            {selected.highlights && selected.highlights.length > 0 ? (
              <Field label="Highlights">
                <div className="flex flex-wrap gap-2">
                  {selected.highlights.map((item) => (
                    <span
                      key={item}
                      className="text-xs font-semibold text-accent-900 bg-accent-100 border border-accent-300 px-2.5 py-1 rounded-full"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </Field>
            ) : null}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Status">
                <select
                  value={selected.status}
                  onChange={(event) => update(selected.id, { status: event.target.value })}
                  className={fieldInput}
                >
                  {optionsFrom(FARM_STATUS).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Verification">
                <select
                  value={selected.verification}
                  onChange={(event) => update(selected.id, { verification: event.target.value })}
                  className={fieldInput}
                >
                  {optionsFrom(VERIFICATION_STATUS).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Safety state">
              <select
                value={selected.safety}
                onChange={(event) => update(selected.id, { safety: event.target.value })}
                className={fieldInput}
              >
                {optionsFrom(SAFETY_STATUS).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add a new farm"
        subtitle="New record"
        footer={
          <>
            <button type="button" onClick={() => setCreateOpen(false)} className={btnGhost}>
              Cancel
            </button>
            <button type="button" onClick={createFarm} disabled={saving} className={btnPrimary}>
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
              Create farm
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Farm name">
            <input
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              placeholder="e.g. Sri Lakshmi Farm"
              className={textInput}
            />
          </TextField>
          <TextField label="Farmer">
            <input
              value={draft.farmer_name}
              onChange={(event) => setDraft({ ...draft, farmer_name: event.target.value })}
              placeholder="e.g. Ramesh Naidu"
              className={textInput}
            />
          </TextField>
          <TextField label="Village">
            <input
              value={draft.village}
              onChange={(event) => setDraft({ ...draft, village: event.target.value })}
              placeholder="e.g. Ravulapalem"
              className={textInput}
            />
          </TextField>
          <TextField label="District">
            <input
              value={draft.district}
              onChange={(event) => setDraft({ ...draft, district: event.target.value })}
              placeholder="e.g. East Godavari"
              className={textInput}
            />
          </TextField>
          <TextField label="Crop / type">
            <input
              value={draft.crop}
              onChange={(event) => setDraft({ ...draft, crop: event.target.value })}
              placeholder="e.g. Paddy"
              className={textInput}
            />
          </TextField>
          <TextField label="Area">
            <input
              value={draft.area}
              onChange={(event) => setDraft({ ...draft, area: event.target.value })}
              placeholder="e.g. 4 acres"
              className={textInput}
            />
          </TextField>
          <TextField label="Established">
            <input
              value={draft.established}
              onChange={(event) => setDraft({ ...draft, established: event.target.value })}
              placeholder="e.g. 1998"
              className={textInput}
            />
          </TextField>
          <TextField label="Capacity (guests)">
            <input
              type="number"
              min={0}
              value={draft.capacity}
              onChange={(event) => setDraft({ ...draft, capacity: Number(event.target.value) })}
              placeholder="e.g. 20"
              className={textInput}
            />
          </TextField>
          <TextField label="Status">
            <select
              value={draft.status}
              onChange={(event) => setDraft({ ...draft, status: event.target.value })}
              className={fieldInput}
            >
              {optionsFrom(FARM_STATUS).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </TextField>
          <TextField label="Verification">
            <select
              value={draft.verification}
              onChange={(event) => setDraft({ ...draft, verification: event.target.value })}
              className={fieldInput}
            >
              {optionsFrom(VERIFICATION_STATUS).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </TextField>
          <TextField label="Safety">
            <select
              value={draft.safety}
              onChange={(event) => setDraft({ ...draft, safety: event.target.value })}
              className={fieldInput}
            >
              {optionsFrom(SAFETY_STATUS).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </TextField>
        </div>
      </Modal>
      {node}
    </div>
  );
}